import sfAuth from "~/auth/SFAuth";
import Config from "~/config";
import unauthorizedRetryPolicy from "~/helpers/unauthorizedRetryPolicy";
import authorizationHeader from "~/services/authorizationHeader";

const UNAUTHORIZED_STATUS = 401;

const resolveApiBase = (): URL => {
	const stripped = Config.endpoint.replace(/\/graphql\/?$/, "");
	return new URL(stripped.endsWith("/") ? stripped : `${stripped}/`);
};

const resolveAgainstApi = (target: string): URL => new URL(target, resolveApiBase());

const fetchWithAuthRetry = async (target: string): Promise<Response> => {
	const url = resolveAgainstApi(target);
	if (url.origin !== resolveApiBase().origin) {
		throw new Error(`Refused authenticated fetch off the API origin (${url.origin})`);
	}

	const send = (token: string) =>
		fetch(url.toString(), { headers: { Authorization: authorizationHeader(token) }, method: "GET" });

	const token = await sfAuth.checkSession();
	if (!token) throw new Error(`Not signed in (status ${UNAUTHORIZED_STATUS})`);

	let response = await send(token);
	if (response.status === UNAUTHORIZED_STATUS) {
		const refreshedToken = await sfAuth.checkSession(true);
		const resolution = await unauthorizedRetryPolicy.resolveUnauthorized(
			{ operationType: "query", tokenRefreshed: !!refreshedToken },
			{ redirectToLogin: () => sfAuth.redirectToLogin(), replay: async () => send(refreshedToken) }
		);
		if (resolution.action !== "replayRequest") throw new Error(`Session ended (status ${response.status})`);
		response = resolution.replayed;
	}

	return response;
};

export default fetchWithAuthRetry;
